import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart3, Users, Eye, TrendingUp, Globe } from "lucide-react";

const PATH_LABELS = {
  "/": "Homepage (root)",
  "/book": "Discovery Call page",
  "/discover": "Needs Assessment",
  "/testimonials": "Testimonial Wall",
  "/login": "Login",
};

const StatCard = ({ label, value, sub, icon: Icon, testid }) => (
  <Card
    data-testid={testid}
    className="p-5 bg-white border border-[#EBE3D2] rounded-2xl hover:-translate-y-[1px] transition-transform"
  >
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{label}</div>
      <Icon className="w-4 h-4 text-[#C28A2E]" />
    </div>
    <div className="mt-3 font-display text-3xl font-semibold text-[#2A1810]">{value}</div>
    {sub && <div className="text-xs text-[#6B5944] mt-1">{sub}</div>}
  </Card>
);

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-[#6B5944]">Loading traffic data…</div>;
  }
  if (!data) return null;

  const maxVisits = Math.max(1, ...data.trend_30d.map((d) => d.visits));
  const maxPathVisits = Math.max(1, ...data.by_path.map((p) => p.visits));

  return (
    <div className="space-y-8 pb-12">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Traffic Analytics</div>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-2">
          Who's visiting your site
        </h1>
        <p className="text-[#6B5944] mt-2 max-w-2xl text-sm">
          Anonymous, session-based traffic counts. No personal information is collected — a random session ID
          identifies each browser session so we can distinguish repeat visits from unique visitors.
        </p>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard testid="stat-today-visits" label="Today · Visits" value={data.today.total} icon={Eye} />
        <StatCard testid="stat-today-unique" label="Today · Unique" value={data.today.unique} icon={Users} />
        <StatCard testid="stat-week-visits" label="This Week · Visits" value={data.week.total} icon={Eye} />
        <StatCard testid="stat-week-unique" label="This Week · Unique" value={data.week.unique} icon={Users} />
        <StatCard testid="stat-month-visits" label="30 Days · Visits" value={data.month.total} icon={Eye} />
        <StatCard testid="stat-month-unique" label="30 Days · Unique" value={data.month.unique} icon={Users} />
      </div>

      {/* 30-day trend */}
      <Card className="p-6 bg-white border border-[#EBE3D2] rounded-2xl">
        <div className="flex items-center gap-2 mb-5">
          <TrendingUp className="w-4 h-4 text-[#C28A2E]" />
          <h2 className="font-display text-lg font-semibold text-[#2A1810]">30-day trend</h2>
          <span className="text-xs text-[#6B5944] ml-auto">Bar = daily visits · line = unique sessions</span>
        </div>
        <div className="flex items-end gap-1.5 h-40" data-testid="trend-chart">
          {data.trend_30d.map((d) => {
            const h = Math.max(2, (d.visits / maxVisits) * 100);
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-[#C28A2E] to-[#F5D88A] transition-all group-hover:from-[#5D3A1F] cursor-default"
                  style={{ height: `${h}%` }}
                  title={`${d.date} · ${d.visits} visits · ${d.unique} unique`}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-[#6B5944]">
          <span>{data.trend_30d[0]?.date}</span>
          <span>{data.trend_30d[Math.floor(data.trend_30d.length / 2)]?.date}</span>
          <span>{data.trend_30d[data.trend_30d.length - 1]?.date} (today)</span>
        </div>
      </Card>

      {/* By path breakdown */}
      <Card className="p-6 bg-white border border-[#EBE3D2] rounded-2xl">
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 className="w-4 h-4 text-[#C28A2E]" />
          <h2 className="font-display text-lg font-semibold text-[#2A1810]">Visits by page · last 30 days</h2>
        </div>
        {data.by_path.length === 0 ? (
          <div className="text-sm text-[#6B5944] py-6 text-center">
            No pageviews recorded yet. Once prospects visit your site, they'll appear here.
          </div>
        ) : (
          <div className="space-y-3" data-testid="by-path-list">
            {data.by_path.map((row) => {
              const w = (row.visits / maxPathVisits) * 100;
              const friendly = PATH_LABELS[row.path] || row.path;
              return (
                <div key={row.path} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 items-center">
                  <div>
                    <div className="text-sm text-[#2A1810] font-medium truncate flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-[#C28A2E] shrink-0" />
                      {friendly}
                    </div>
                    <div className="text-[11px] text-[#6B5944] truncate">{row.path}</div>
                    <div className="mt-2 h-2 rounded-full bg-[#F5EFDF] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#C28A2E] to-[#F5D88A]" style={{ width: `${w}%` }} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-semibold text-[#2A1810]">{row.visits}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B5944]">
                      {row.unique_visitors} unique
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AnalyticsPage;
